import { Star, StarSolid } from "@ignite-analytics/icons";
import { Box, RatingProps, styled, Typography } from "@mui/material";
import React from "react";
import { FormattedMessage } from "react-intl";

interface StyledRatingProps extends RatingProps {
    showLabels?: boolean;
}

const RatingGridContainer = styled(Box)<{ labeled?: boolean }>(({ labeled }) => ({
    display: "flex",
    gap: labeled ? "32px" : "4px",
    alignItems: "flex-start",
}));

const StarLabelColumn = styled(Box)({
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    alignItems: "center",
});

const StarButton = styled(Box)<{ filled?: boolean; activeHover?: boolean; disabled?: boolean }>(
    ({ filled, activeHover, disabled, theme }) => ({
        fontSize: "24px",
        color: filled ? "#FFB400" : theme.palette.grey[400],
        cursor: disabled ? "not-allowed" : "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transform: activeHover && !disabled ? "scale(1.2)" : "scale(1)",
        transition: "transform 0.15s ease-in-out",
        opacity: disabled ? 0.6 : 1,
    })
);

const LabelText = styled(Typography)(({ theme }) => ({
    fontSize: "12px",
    color: theme.palette.text.secondary,
    textAlign: "center",
    whiteSpace: "nowrap",
}));

// Only show our localized quality labels for 5 star ratings for now.
const getRatingLabels = (ratingMax: number): React.ReactNode[] | null => {
    if (ratingMax === 5) {
        return [
            <FormattedMessage key="rating-1" defaultMessage="Poor" />,
            <FormattedMessage key="rating-2" defaultMessage="Weak" />,
            <FormattedMessage key="rating-3" defaultMessage="Fair" />,
            <FormattedMessage key="rating-4" defaultMessage="Good" />,
            <FormattedMessage key="rating-5" defaultMessage="Great" />,
        ];
    }
    return null;
};

export const StyledRating: React.FC<StyledRatingProps> = (props) => {
    const { showLabels, max = 5, value, onChange, disabled } = props;
    const [hoverValue, setHoverValue] = React.useState<number>(-1);

    const handleClick = (newValue: number) => {
        if (disabled) return;
        if (onChange) {
            onChange({} as React.SyntheticEvent, newValue);
        }
    };

    const labels = getRatingLabels(max);

    return (
        <RatingGridContainer labeled={showLabels} onMouseLeave={() => !disabled && setHoverValue(-1)}>
            {Array.from({ length: max }, (_, index) => {
                const ratingValue = index + 1;
                const isSelected = value !== undefined && value !== null && value >= ratingValue;
                const isHovered = !disabled && hoverValue >= ratingValue;
                const isActiveHover = !disabled && hoverValue === ratingValue;
                // When hovering, show only hovered stars; otherwise show selected stars
                const isFilled = hoverValue !== -1 ? isHovered : isSelected;

                return (
                    <StarLabelColumn key={ratingValue} onMouseEnter={() => !disabled && setHoverValue(ratingValue)}>
                        <StarButton
                            filled={isFilled}
                            activeHover={isActiveHover}
                            disabled={disabled}
                            onClick={() => handleClick(ratingValue)}
                        >
                            {isFilled ? <StarSolid fontSize="inherit" /> : <Star fontSize="inherit" />}
                        </StarButton>
                        {showLabels && labels && <LabelText>{labels[index]}</LabelText>}
                    </StarLabelColumn>
                );
            })}
        </RatingGridContainer>
    );
};
